import {
  fetchUserAttributes,
  getCurrentUser,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  fetchAuthSession,
  autoSignIn,
} from "aws-amplify/auth";
import { AuthUser } from "@models/auth-user";
import { SignInOutput } from "@aws-amplify/auth";
import { jwtDecode } from "jwt-decode";
import {
  PatientTokenRefreshError,
  TokenRefreshServiceService,
} from "@service/patient-auth.service";
import i18n from 'i18next';
import { AnalyticsService } from '@service/analytics.service';
import { MobileService } from '@service/mobile.service';

export type SocialLoginType = "Google" | "Facebook" | "Apple";

const PATIENT_ACCESS_TOKEN_STORAGE_KEY = "accessToken";

interface PatientAccessToken {
  token: string;
  expiresAt: number;
}

export class AuthService {
  public static readonly instance = new AuthService();
  private tokenRefreshService = TokenRefreshServiceService.instance;
  private patientAccessToken: PatientAccessToken | null = null;
  private isRefreshing = false;

  get isPatient(): boolean {
    return !!this.patientAccessToken;
  }

  private constructor() {
    const token = localStorage.getItem(PATIENT_ACCESS_TOKEN_STORAGE_KEY);
    if (token) {
      this.patientAccessToken = {
        token,
        expiresAt: this.getTokenExpirationTime(token),
      };
    }
  }

  setAccessToken(accessToken: string): void {
    if (accessToken) {
      localStorage.setItem(PATIENT_ACCESS_TOKEN_STORAGE_KEY, accessToken);
      this.patientAccessToken = {
        token: accessToken,
        expiresAt: this.getTokenExpirationTime(accessToken),
      };
      MobileService.instance.auth({
        accessToken: accessToken,
        expirationDate: this.getTokenExpirationTime(accessToken)
      });
    }
  }

  /**
   * Returns a `Bearer …` value for API calls.
   *
   * For Cognito, `fetchAuthSession()` can **resolve with no `tokens`** (Amplify returns
   * `null` from the token store / refresh in several cases without throwing), or the call
   * can throw on transient failures. Callers that still have an app session can pass
   * `{ forceRefresh: true }` for a second attempt after an empty first read.
   */
  async getAccessToken(options?: { forceRefresh?: boolean }): Promise<string> {
    let accessToken: string | null = null;
    const forceRefresh = options?.forceRefresh ?? false;

    if (this.isPatient) {
      try {
        accessToken = await this.getPatientAccessToken();
      } catch (e) {
        if (
          e instanceof PatientTokenRefreshError &&
          e.kind === "transient"
        ) {
          accessToken = this.patientAccessToken?.token ?? null;
        } else {
          this.patientAccessToken = null;
          localStorage.removeItem(PATIENT_ACCESS_TOKEN_STORAGE_KEY);
          accessToken = null;
        }
      }
    } else {
      try {
        const { tokens } = await fetchAuthSession({ forceRefresh });
        if (tokens) {
          accessToken = tokens.accessToken.toString();
        }
      } catch {
        accessToken = null;
      }
    }
    return accessToken ? `Bearer ${accessToken}` : "";
  }

  async confirmResetPassword(
    email: string,
    code: string,
    password: string,
  ): Promise<void> {
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword: password,
    });
  }

  async sendResetPasswordCode(email: string): Promise<void> {
    await resetPassword({ username: email });
  }

  async signUp(email: string, password: string): Promise<void> {
    const { nextStep } = await signUp({
      username: email,
      password,
      options: { autoSignIn: true, userAttributes: { email, locale: i18n.language || 'en-US' } } ,
    });

    if (nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
      await autoSignIn();
    }
  }

  async sendVerificationCode(email: string): Promise<void> {
    await resendSignUpCode({ username: email });
  }

  async confirmSignUp(email: string, code: string): Promise<boolean> {
    const { isSignUpComplete, nextStep } = await confirmSignUp({
      confirmationCode: code,
      username: email,
    });

    if (nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
      await autoSignIn();
    }

    return isSignUpComplete;
  }

  async signWithEmailAndPassword(
    email: string,
    password: string,
  ): Promise<SignInOutput> {
    return signIn({
      username: email,
      password,
      options: {
        authFlowType: "USER_PASSWORD_AUTH",
      },
    });
  }

  async signInSocial(type: SocialLoginType): Promise<void> {
    await signInWithRedirect({
      provider: type,
    });
  }

  async loadCurrentUser(): Promise<AuthUser> {
    const currentUser = await getCurrentUser();
    const attributes = await fetchUserAttributes();
    AnalyticsService.instance.setUser(currentUser.userId, currentUser.username, attributes.email || '');
    return {
      id: currentUser.userId,
      username: currentUser.username,
      email: attributes.email || "",
      firstName: attributes.given_name || "",
      lastName: attributes.family_name || "",
      verified: attributes.email_verified === "true",
      isPatient: false,
    };
  }

  async signOut(): Promise<void> {
    await signOut();
    this.patientAccessToken = null;
    localStorage.removeItem(PATIENT_ACCESS_TOKEN_STORAGE_KEY);
    AnalyticsService.instance.clearUser();
  }

  private async getPatientAccessToken(): Promise<string | null> {
    if (!this.patientAccessToken) {
      return null;
    }

    if (this.patientAccessToken.expiresAt <= Date.now()) {
      if (!this.isRefreshing) {
        try {
          this.isRefreshing = true;
          const { accessToken } = await this.tokenRefreshService.refreshToken(
            this.patientAccessToken.token,
          );
          this.setAccessToken(accessToken);
        } finally {
          this.isRefreshing = false;
        }
      } else {
        await new Promise((resolve, reject) => {
          const interval = setInterval(() => {
            if (!this.isRefreshing) {
              clearInterval(interval);
              if (this.patientAccessToken) {
                resolve(this.patientAccessToken.token);
              } else {
                reject();
              }
            }
          }, 100);
        });
      }
    }

    return this.patientAccessToken.token;
  }

  private getTokenExpirationTime(accessToken: string): number {
    const decoded = jwtDecode(accessToken);
    return (decoded.exp || Date.now() + 60 * 60) * 1000;
  }
}
